import { alert, alertPrefs, newFor, seedKnown, soundReady, unlockSound } from './alerts.js';
import { createBell } from './bell.js';
import { liveSource } from './live-source.js';
import { assistantButton } from './assistant-panel.js';
import { offsetMs, zonedParts } from '../shared/timezone.js';
import { getZone, setZone } from '../shared/zone-state.js';
import { DOCS, fillHelp } from './help.js';
import { visitSource } from '../shared/traffic-source.js';

// A tab opened with Users → Log in as works as that team member: its own session token (kept in this tab only) goes with every request,
// so the Owner's other tabs stay the Owner's.
const asToken = (() => {
  try {
    return sessionStorage.getItem('kv_as');
  } catch {
    return null;
  }
})();
if (asToken) {
  const nativeFetch = window.fetch.bind(window);
  window.fetch = (input, init = {}) => {
    const raw = typeof input === 'string' ? input : input?.url || '';
    let target;
    try {
      target = new URL(raw, location.href);
    } catch {
      return nativeFetch(input, init);
    }
    if (target.origin !== location.origin || !target.pathname.startsWith('/api/')) return nativeFetch(input, init);
    const headers = new Headers(init.headers || (input instanceof Request ? input.headers : undefined));
    headers.set('x-kv-as', asToken);
    return nativeFetch(input, { ...init, headers });
  };
  const NativeSource = window.EventSource;
  if (NativeSource) {
    window.EventSource = class extends NativeSource {
      constructor(url, options) {
        super(`${url}${String(url).includes('?') ? '&' : '?'}as=${encodeURIComponent(asToken)}`, options);
      }
    };
  }
}

// The time zone every time and date is shown in: the person's own (My account), UTC until they choose.
export { getZone, setZone };
/** How far the zone's clock is ahead of UTC now (ms), and the same as the `tz` of a request: minutes WEST of UTC, so the server can put days in this zone. */
export const zoneOffset = () => offsetMs(Date.now(), getZone());
export const tzParam = () => String(-Math.round(zoneOffset() / 60000));
/** A moment as a date and time (toLocaleString options), a date, or a time, in the person's time zone. */
export const fmt = (ts, options) => new Date(ts).toLocaleString(undefined, { timeZone: getZone(), ...options });
export const fmtDate = (ts, options) => new Date(ts).toLocaleDateString(undefined, { timeZone: getZone(), ...options });
export const fmtTime = (ts, options) => new Date(ts).toLocaleTimeString(undefined, { timeZone: getZone(), ...options });
/** Asks the hub. `signal`: an AbortController's, to give up an answer that is no longer wanted (the hub then does not start it, or stops it): that throws an error named AbortError. */
export async function api(path, { method = 'GET', body, signal } = {}) {
  const res = await fetch(`/api${path}`, {
    method,
    signal,
    headers: body ? { 'content-type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401 && !path.startsWith('/auth/login') && !path.startsWith('/auth/2fa/')) { // (a wrong code is a 401 too, but not a lost session)
    // Logged out (or the session expired): back to the login page, then return here.
    location.href = `login.html?next=${encodeURIComponent(location.pathname + location.search + location.hash)}`;
    return new Promise(() => {});
  }
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw Object.assign(new Error(data?.error || `${res.status} ${res.statusText}`), { status: res.status, data });
  }
  return res.status === 204 ? null : res.json();
}

export function compact(n) {
  return new Intl.NumberFormat(undefined, { notation: n >= 10000 ? 'compact' : 'standard', maximumFractionDigits: 1 }).format(n);
}

/** Status pill for a website: paused / receiving data / waiting for the first recording. */
export function websiteStatus(w) {
  if (w.paused) return { label: 'Paused', tone: 'idle' };
  if (w.online > 0) return { label: `${w.online} online`, tone: 'good' };
  if (!w.last_seen) return { label: 'Waiting for data', tone: 'warn' };
  if (Date.now() - w.last_seen < 5 * 60 * 1000) return { label: 'Live now', tone: 'good' };
  return { label: 'Recording', tone: 'good' };
}

export function pill({ label, tone }) {
  return el('span', { class: `pill pill-${tone}` }, label);
}

export function duration(ms) {
  const s = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

export function dateTime(ts) {
  return fmt(ts, { dateStyle: 'medium', timeStyle: 'short' });
}

/** With the seconds: Oct 6, 2026, 9:19:07 AM. */
export function dateTimeSeconds(ts) {
  return fmt(ts, { dateStyle: 'medium', timeStyle: 'medium' });
}

export function timeAgo(ts) {
  const s = Math.round((Date.now() - ts) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  // Older: "Sep 24, 3:55 PM" (the year only when it isn't this year). dateTime() has the full date.
  const sameYear = zonedParts(ts, getZone()).year === zonedParts(Date.now(), getZone()).year;
  return fmt(ts, { month: 'short', day: 'numeric', year: sameYear ? undefined : 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** "https://site.com/pricing?utm_source=x#plans" → "/pricing#plans". The query (campaign
 * parameters…) is left out: those show as tags, and the full address is in the tooltip. */
export function shortUrl(url) {
  if (!url) return '(unknown page)';
  try {
    const u = new URL(url);
    return u.pathname + u.hash;
  } catch {
    return url;
  }
}

/** The "Source" cell of a visit: the referrer's website, else the ad or campaign in the page's address, else Direct (the tooltip says how it was found). */
export function sourceCell(referrer, url) {
  const { name, why } = visitSource({ referrer, url });
  return el('td', { class: 'ref', title: why }, name);
}

export function host(url) {
  if (!url) return '';
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/**
 * Makes a "↻ Refresh" button reload the data of its page: `reload()` may return a promise. The button is off while it works, for at
 * least half a second so the click is seen.
 */
export function wireRefresh(button, reload) {
  const label = button.textContent || '↻ Refresh';
  button.addEventListener('click', async () => {
    if (button.disabled) return;
    button.disabled = true;
    button.textContent = '↻ Refreshing…';
    const started = Date.now();
    try {
      await reload();
    } catch {
      // the page shows its own error
    }
    setTimeout(() => {
      button.disabled = false;
      button.textContent = label;
    }, Math.max(0, 500 - (Date.now() - started)));
  });
  return button;
}

export const refreshButton = (reload) => wireRefresh(el('button', { type: 'button', class: 'btn-small refresh-btn', title: 'Load the latest data' }, '↻ Refresh'), reload);

/** Where a recording opens: in a new tab, so the list you came from stays as it is. */
export const openRecording = (href) => window.open(href, '_blank', 'noopener');

const BOOLEAN_ATTRS = new Set(['disabled', 'hidden', 'checked', 'selected', 'readonly', 'required', 'open', 'multiple', 'autofocus']);
export function el(tag, props = {}, ...children) {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(props)) {
    if (value == null) continue;
    if (key === 'class') node.className = value;
    else if (key.startsWith('on')) node.addEventListener(key.slice(2), value);
    else if (BOOLEAN_ATTRS.has(key) && typeof value === 'boolean') { if (value) node.setAttribute(key, ''); } // disabled: false is not disabled
    else if ((key === 'href' || key === 'src' || key === 'action' || key === 'formaction') && /^[\s\u0000-\u001f]*(javascript|vbscript):/i.test(String(value))) continue; // (an address a visitor made up must never run as a script)
    else node.setAttribute(key, value);
  }
  for (const child of children.flat()) {
    if (child != null) node.append(child instanceof Node ? child : String(child));
  }
  return node;
}

/**
 * A clear way back out of a page: "← Back to …" at the top of the page's content (or of `into`), leading to the screen it was opened
 * from (the breadcrumbs say where you are; this is the one-click way out). Replaces an earlier one if the page asks again.
 */
export function addBackLink(href, label, { into } = {}) {
  const box = into || document.querySelector('main');
  if (!box) return;
  box.querySelector(':scope > .back-link')?.remove();
  box.prepend(el('a', { class: 'back-link', href }, el('span', { 'aria-hidden': 'true' }, '←'), ` Back to ${label}`));
}

const TAB_NAMES = { overview: 'the website', live: 'Live', recordings: 'Recordings', heatmaps: 'Heatmaps', surveys: 'Surveys', tests: 'A/B tests', events: 'Events', funnels: 'Funnels', paths: 'Paths', forms: 'Forms', links: 'Short links', backlinks: 'Backlinks', settings: 'Settings' };
const PAGE_NAMES = { 'inbox.html': 'the Inbox', 'visitor.html': 'the visitor', 'session.html': 'the recording', 'test.html': 'the test', 'survey.html': 'the survey', 'marketing.html': 'Marketing', 'affiliates.html': 'Affiliates', 'users.html': 'Users', 'heatmap.html': 'the heatmap' };

/** What a dashboard address is called, for "Back to …": a website's tabs by name, other pages by what they show. */
export function pageLabel(url) {
  const file = url.pathname.split('/').pop();
  if (file === 'website.html') return TAB_NAMES[url.hash.slice(1).split('/')[0]] || 'the website';
  if (!file || file === 'index.html') return 'websites';
  return PAGE_NAMES[file] || 'the previous page';
}

/**
 * For a page that is opened from many places (a recording, a visitor): "← Back to …" leads to the dashboard page it was opened from
 * (with its filters and tab), else to `fallbackHref`.
 */
export function addBackLinkFromReferrer(fallbackHref, fallbackLabel, options) {
  try {
    const from = new URL(document.referrer);
    const folder = location.pathname.replace(/[^/]*$/, '');
    if (from.origin === location.origin && from.pathname.startsWith(folder) && from.pathname !== location.pathname) return addBackLink(from.href, pageLabel(from), options);
  } catch {}
  addBackLink(fallbackHref, fallbackLabel, options);
}

/** True when the user's role includes the permission (e.g. 'websites.manage'). */
export function can(user, permission) {
  return !!user && user.permissions.includes(permission);
}

// ------------------------------------------------------------------ profile pictures

export function initialsOf(name) {
  return String(name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/** A round picture of someone ({ name, avatar }): their photo, or their initials. */
export function avatarEl(person, extraClass = '') {
  const span = el('span', { class: `avatar${extraClass ? ` ${extraClass}` : ''}`, 'aria-hidden': 'true' });
  if (person?.avatar) span.append(el('img', { src: person.avatar, alt: '' }));
  else span.textContent = initialsOf(person?.name);
  return span;
}

/** A picture file made into a 256 px square (the middle of it), as a JPEG. */
export async function squarePicture(file, size = 256) {
  if (!/^image\//.test(file.type)) throw new Error('Choose an image (JPEG, PNG, WebP…)');
  const bitmap = await createImageBitmap(file).catch(() => {
    throw new Error('This image can’t be read. Try a JPEG or PNG.');
  });
  const side = Math.min(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, size, size);
  ctx.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, size, size);
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.88));
}

/**
 * Uploads a profile picture to `url` (e.g. /auth/me/avatar) after the person has put it where they want it in its circle (picture-crop.js).
 * Returns the updated user, or null when they cancelled.
 */
export async function uploadPicture(url, file) {
  const blob = await import('./picture-crop.js').then((m) => m.positionPicture(file));
  if (!blob) return null;
  const res = await fetch(`/api${url}`, { method: 'POST', headers: { 'content-type': 'image/jpeg' }, body: blob });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error || 'Could not upload the picture');
  return data;
}

/**
 * A short notice at the bottom of the screen ("Saved", or an error), so saving always shows,
 * even when the page redraws right after.
 */
export function toast(text, tone = 'good') {
  let box = document.getElementById('toast');
  if (!box) {
    box = el('div', { id: 'toast', class: 'toast', role: 'status', 'aria-live': 'polite' });
    document.body.append(box);
  }
  // an open window (a <dialog>) covers everything in the page: the message goes inside it, so it is seen there too
  const host = [...document.querySelectorAll('dialog[open]')].pop() || document.body;
  if (box.parentNode !== host) host.append(box);
  box.textContent = tone === 'good' ? `✓ ${text}` : text;
  box.className = `toast toast-${tone} show`;
  clearTimeout(box.timer);
  box.timer = setTimeout(() => box.classList.remove('show'), tone === 'good' ? 2500 : 6000);
}

/**
 * Loads the logged-in user and adds the user menu (Users, My account, Log out) to the top bar.
 * Every dashboard page calls this first.
 */
export async function initPage() {
  const user = await api('/auth/me');
  setZone(user.timezone);
  const topbar = document.querySelector('.topbar');
  const menu = el(
    'details',
    { class: 'user-menu' },
    el(
      'summary',
      { 'aria-label': 'Account menu' },
      avatarEl(user),
      el('span', { class: 'user-name' }, user.name),
    ),
    el(
      'div',
      { class: 'menu' },
      el('div', { class: 'menu-head' }, el('strong', {}, user.name), el('span', { class: 'muted' }, `${user.email} · ${user.roleLabel}`),
        user.account ? el('span', { class: 'muted menu-company' }, user.account.name) : null),
      el('a', { href: 'account.html' }, 'My account'),
      user.freelancer || user.role === 'owner' ? el('a', { href: 'payments.html', id: 'menu-payments' }, 'Freelancer Payments') : null,
      el('a', { href: 'desktop-app.html' }, 'Kolvantage apps'),
      el('a', { href: DOCS, target: '_blank', rel: 'noopener' }, 'Help & documentation'),
      el('button', { type: 'button', onclick: logout }, 'Log out'),
      el('div', { class: 'menu-version muted', id: 'hub-version' }),
    ),
  );
  // The Owner sees how many invoices wait for them, on the Payments item.
  if (user.role === 'owner') {
    api('/payments/count').then((r) => { if (r.review) menu.querySelector('#menu-payments')?.append(el('span', { class: 'menu-badge' }, String(r.review))); }).catch(() => {});
  }
  // Which version is running (handy after an update): from /health.
  fetch('/health')
    .then((r) => r.json())
    .then((h) => {
      if (h.environment) markEnvironment(h.environment);
      if (!h.version && !h.build) return;
      pageVersion = h.build || h.version;
      const built = h.builtAt ? ` · updated ${fmtDate(h.builtAt, { month: 'short', day: 'numeric', year: 'numeric' })}` : '';
      const box = menu.querySelector('#hub-version');
      box.textContent = `Version ${h.version || h.build}${built}`;
      if (h.build) box.title = `Build ${h.build}`; // the exact code, for support
    })
    .catch(() => {});
  document.addEventListener('click', (e) => {
    if (!menu.contains(e.target)) menu.open = false;
  });
  import('./sidebar.js').then((m) => m.mountSidebar(user, topbar)).catch(() => {}); // the menu on the left
  if (can(user, 'chat.reply')) topbar.append(inboxBar(user));
  topbar.append(createBell({ api, el, avatarEl, toast })); // Boards notifications: shown for people who have a board
  topbar.append(assistantButton());
  // Ctrl+Shift+A+S (⌘+Shift+A+S on a Mac), the four keys together: take a screenshot, on any page (the tool loads only when it is used).
  // (the physical keys A and S, so it works in any keyboard language; Ctrl+Shift with A or S alone is taken too, so the browser's own use of it, e.g. Chrome's tab search, does not open first)
  // In the Desktop app the app itself also takes a shortcut from anywhere, and sends the picture of the screen as the kv-screenshot event.
  // the Android app: a newer app file on the hub is shown to the person (and required after a wait, like the Desktop app)
  if (/KolvantageMobile\//.test(navigator.userAgent)) {
    import('./mobile-update.js').then((m) => m.watchMobileUpdate()).catch(() => {});
    import('./mobile-push.js').then((m) => m.registerPhonePush()).catch(() => {}); // phone notifications (from app 0.2.0)
    import('./mobile-save.js').then((m) => m.watchDownloads()).catch(() => {}); // files saved from the app: the share sheet (from app 0.3.0, both phones)
  }
  // the Desktop app chose the area of a screen recording and sends what to record: the page records it (screen-record.js recordFromApp)
  addEventListener('kv-record-start', (e) => import('./screen-record.js').then((m) => m.recordFromApp(e.detail)).catch(() => {}));
  // (the app sends { image, cropped: true }: the area was chosen on the screen already; an older app sends the whole screen as a plain address, to choose from here)
  addEventListener('kv-screenshot', (e) => import('./screenshot.js').then(async (m) => {
    const d = e.detail;
    const cropped = typeof d === 'object' && d?.cropped;
    const canvas = cropped && d.pieces ? await m.canvasFromPieces(d) : await m.canvasFromDataUrl(cropped ? d.image : d); // (the area can cover several screens: the app sends a piece of each)
    return m.startScreenshot(canvas, cropped ? {} : { choose: true });
  }).catch(() => {}));
  const held = new Set();
  addEventListener('keydown', (e) => {
    if (!(e.ctrlKey || e.metaKey) || !e.shiftKey || e.altKey || (e.code !== 'KeyA' && e.code !== 'KeyS')) return;
    e.preventDefault();
    held.add(e.code);
    if (held.has('KeyA') && held.has('KeyS')) {
      held.clear();
      import('./screenshot.js').then((m) => m.startScreenshot()).catch(() => {});
    }
  });
  // Ctrl+Alt+Shift+X (⌘+Option+Shift+X on a Mac): start the Screen recorder, or stop the recording that is going on
  addEventListener('keydown', (e) => {
    if ((e.ctrlKey || e.metaKey) && e.altKey && e.shiftKey && e.code === 'KeyX') {
      e.preventDefault();
      import('./screen-record.js').then((m) => m.toggleRecording()).catch(() => {});
    }
  });
  addEventListener('keyup', (e) => { held.delete(e.code); });
  addEventListener('blur', () => held.clear());
  // tell the hub every 30 seconds that this person is here and on what (the Owner sees it in Users); a hidden tab stays quiet
  const beat = () => { if (!document.hidden) fetch('/api/me/presence', { method: 'POST', credentials: 'same-origin' }).catch(() => {}); };
  beat();
  setInterval(beat, 30000);
  document.addEventListener('visibilitychange', beat);
  topbar.append(menu); // Help & documentation is in this menu
  // A page that is opened from Home (or Tools) says so in its <body>: data-back-href, data-back-to and, for a page with a side menu, data-back-into.
  const { backHref, backTo, backInto } = document.body.dataset;
  if (backHref && backTo) addBackLink(backHref, backTo, backInto ? { into: document.querySelector(backInto) } : undefined);
  fillHelp();
  watchForUpdates();

  // A Kolvantage manager who logged in as this person (Kolvantage management → Companies → Log in as): a bar that can't be missed.
  if (user.impersonation?.kind === 'owner') {
    topbar.before(
      el('div', { class: 'impersonation-bar', role: 'status' },
        el('span', {}, el('strong', {}, `You are logged in as ${user.name}`), ' in this tab. Everything you do here is done as them. Your own dashboard stays open in your other tabs.'),
        el('button', { type: 'button', class: 'btn-small', onclick: async () => {
          await api('/auth/impersonation/exit', { method: 'POST' }).catch(() => {});
          try { sessionStorage.removeItem('kv_as'); } catch { /* ignore */ }
          window.close();
          location.href = '/dashboard/';
        } }, 'Exit and go back to my own login')),
    );
  } else if (user.impersonation) {
    topbar.before(
      el('div', { class: 'impersonation-bar', role: 'status' },
        el('span', {}, el('strong', {}, `You are logged in as ${user.name}`), user.account ? ` (${user.account.name}) as Kolvantage management. Everything you do is done as them.` : ' as Kolvantage management.'),
        el('button', { type: 'button', class: 'btn-small', onclick: async () => {
          const r = await api('/auth/impersonation/exit', { method: 'POST' });
          location.href = r.next;
        } }, 'Exit and go back to management')),
    );
  }

  // Logins from before emails (like "admin") keep working until an email is set.
  if (user.emailMissing && !location.pathname.endsWith('/account.html')) {
    topbar.after(
      el(
        'div',
        { class: 'notice', role: 'status' },
        el('span', {}, el('strong', {}, 'Add your email address. '), 'From now on you log in with your email instead of a username.'),
        el('a', { class: 'btn btn-small', href: 'account.html' }, 'Set email'),
      ),
    );
  }
  return user;
}

/** A test copy of Kolvantage (HUB_ENV=qa) says so: a label in the top bar and in the tab's title. Nothing else is different. */
function markEnvironment(name) {
  const brand = document.querySelector('.topbar .brand');
  if (brand && !document.querySelector('.env-badge')) brand.after(el('span', { class: 'env-badge', title: `This is the ${name.toUpperCase()} copy of Kolvantage, not production` }, name.toUpperCase()));
  if (!document.title.startsWith(`${name.toUpperCase()} · `)) document.title = `${name.toUpperCase()} · ${document.title}`;
}

// ------------------------------------------------------------------ after an update

let pageVersion = null; // the Kolvantage version this page was loaded with
let typed = false; // anything typed on this page (it may be unsaved, so no automatic reload)
let updateShown = false;
// Pages where clicks alone change things (the visual editor) or long forms are edited.
const NO_AUTO_RELOAD = ['/test-visual.html', '/test-edit.html', '/survey-edit.html'];

function watchForUpdates() {
  addEventListener('input', () => (typed = true), true);
  addEventListener('focus', () => checkForUpdate());
  document.addEventListener('visibilitychange', () => checkForUpdate());
  setInterval(checkForUpdate, 5 * 60000);
}

/**
 * Pages opened before Kolvantage was updated keep running the old code (so, for example, alerts
 * may not work as they should). Then a notice asks to reload; a page nobody is looking at, with
 * nothing typed in it, reloads by itself.
 */
export async function checkForUpdate() {
  if (!pageVersion) return;
  let version;
  try {
    const h = await (await fetch('/health', { cache: 'no-store' })).json();
    version = h.build || h.version; // every update changes the build, even without a new release number
  } catch {
    return;
  }
  if (!version || version === pageVersion) return;
  const safe = !typed && !NO_AUTO_RELOAD.some((p) => location.pathname.endsWith(p)) && !document.querySelector('dialog[open]');
  if (document.visibilityState === 'hidden' && safe) return location.reload();
  if (updateShown) return;
  updateShown = true;
  document.querySelector('.topbar')?.after(
    el(
      'div',
      { class: 'notice', role: 'status' },
      el('span', {}, el('strong', {}, 'Kolvantage was updated. '), 'Reload this page to get the newest version (save anything you are editing first).'),
      el('button', { type: 'button', class: 'btn btn-small', onclick: () => location.reload() }, 'Reload'),
    ),
  );
}

/**
 * The Inbox link in the top bar, with how many chats wait and conversations have new messages,
 * and whether you're accepting chats. On every dashboard page (not only the Inbox) the page keeps
 * a live connection, so you stay online for chats and get alerts while you work elsewhere.
 */
function inboxBar(user) {
  const status = el('a', { class: 'status-pill', href: 'inbox.html', hidden: '' });
  const bar = el('span', { class: 'inbox-bar' }, status); // (the Inbox link itself is in the menu on the left, with the number of chats and tickets waiting)
  if (location.pathname.endsWith('/inbox.html')) return bar; // the Inbox has its own switch and connection
  let accepting = null;
  // Browsers play sounds only after a click on the page, so until then the pill says so.
  const showStatus = (value = accepting) => {
    if (value === null || user.inboxScope === 'tickets') return; // no live chat for them: no "accepting chats"
    accepting = value;
    const muted = accepting && alertPrefs().sound && !soundReady();
    status.hidden = false;
    status.className = `status-pill ${accepting ? 'on' : 'off'}`;
    status.textContent = accepting ? `● Accepting chats${muted ? ' 🔇' : ''}` : '○ Away';
    status.title = !accepting
      ? 'You get no new chats. Change it in the Inbox.'
      : muted
        ? 'You get new chats. Click anywhere on this page to turn on the sound for them.'
        : 'You get new chats. Change it in the Inbox.';
  };
  let timer = null;
  const refresh = () =>
    api('/chat/summary')
      .then((s) => {
        const n = s.counts.waiting + s.counts.unread;
        window.kvInbox = { n, hot: s.counts.waiting > 0 };
        dispatchEvent(new CustomEvent('kv-inbox-count', { detail: window.kvInbox })); // (the menu on the left shows it)
        showStatus(s.status === 'accepting');
      })
      .catch(() => {});
  const soon = () => {
    clearTimeout(timer);
    timer = setTimeout(refresh, 400);
  };
  refresh();
  setInterval(() => document.visibilityState === 'visible' && refresh(), 60000);

  // Alerts for chats given to you (or waiting), like in the Inbox; clicking one opens it there.
  const seen = new Map(); // conversation id -> assignee last seen
  seedKnown(seen);
  const open = (id, ticket = false) => (location.href = `inbox.html#${ticket ? 'mytickets' : 'mine'}/${id}`); // a ticket is in My tickets, a chat in Mine
  const unlock = () => unlockSound().then(() => showStatus(), () => showStatus());
  addEventListener('pointerdown', unlock);
  addEventListener('keydown', unlock);
  if (!window.EventSource) return bar;
  const source = liveSource('/api/chat/stream');
  let opened = false;
  source.addEventListener('open', () => {
    if (opened) checkForUpdate(); // reconnected: Kolvantage may have restarted after an update
    opened = true;
  });
  source.addEventListener('conversation', (e) => {
    const c = JSON.parse(e.data);
    const kind = newFor(seen, c, user.id);
    const onClick = () => open(c.id, c.channel !== 'chat');
    // Always a desktop notification for these (even while you look at the page), plus the sound.
    if (kind === 'chat') alert(`New chat for you · ${c.website_name}`, c.preview || 'A visitor started a chat', { tag: `chat-${c.id}`, sticky: true, force: true, onClick });
    else if (kind === 'waiting') alert(`New chat waiting · ${c.website_name}`, c.preview || 'A visitor started a chat', { tag: `chat-${c.id}`, force: true, onClick });
    else if (kind === 'ticket') alert(`New ticket for you · ${c.group_name || c.website_name}`, c.subject || '', { tag: `chat-${c.id}`, force: true, kind: 'ticket', onClick });
    soon();
  });
  // Payments: an invoice arrived for the Owner, or was decided for a freelancer (the phone gets the same, from the server)
  source.addEventListener('paynotice', (e) => {
    const n = JSON.parse(e.data);
    alert(n.title, n.body, { tag: n.tag, force: true, onClick: () => { location.href = 'payments.html'; } });
  });
  source.addEventListener('boardnotice', (e) => dispatchEvent(new CustomEvent('kv-boardnotice', { detail: JSON.parse(e.data) })));
  source.addEventListener('agents', (e) => {
    const mine = JSON.parse(e.data).find((a) => a.id === user.id);
    if (mine) showStatus(mine.status === 'accepting');
  });
  // A new message from the visitor (or customer) in a conversation that's yours or nobody's, like
  // in the Inbox. A brand-new chat's first message is left to the "new chat" alert above.
  source.addEventListener('message', (e) => {
    const { conversationId, message } = JSON.parse(e.data);
    soon();
    if (message?.author !== 'visitor' || !seen.has(conversationId)) return;
    const assignee = seen.get(conversationId);
    if (assignee && assignee !== user.id) return;
    alert('New message', message.body || 'The visitor sent a message', { tag: `chat-${conversationId}`, force: true, onClick: () => open(conversationId) });
  });
  for (const name of ['hidden', 'deleted']) source.addEventListener(name, soon);
  return bar;
}

/**
 * Starts a live chat with someone on the website now: asks for the first message, then opens the
 * chat in the Inbox. The chat box opens on the visitor's screen within about 15 seconds.
 */
export function startChatWith(sessionId) {
  const text = el('textarea', { name: 'message', rows: 3, maxlength: 4000, required: '' });
  text.value = 'Hi! Can I help you with anything?';
  const error = el('p', { class: 'form-error', hidden: '' });
  const send = el('button', { type: 'submit', class: 'btn-primary' }, 'Start chat');
  const dialog = el(
    'dialog',
    {},
    el(
      'form',
      { method: 'dialog' },
      el('h2', {}, 'Start a chat'),
      el('p', { class: 'muted' }, "The chat box opens on the visitor's screen with your message (within about 15 seconds)."),
      el('label', { class: 'field' }, el('span', {}, 'Your message'), text),
      error,
      el('div', { class: 'dialog-actions' }, el('button', { type: 'button', onclick: () => dialog.close() }, 'Cancel'), send),
    ),
  );
  dialog.addEventListener('close', () => dialog.remove());
  dialog.querySelector('form').addEventListener('submit', async (e) => {
    e.preventDefault();
    send.disabled = true;
    error.hidden = true;
    try {
      const r = await api('/chat/invite', { method: 'POST', body: { sessionId, message: text.value } });
      location.href = `inbox.html#mine/${r.conversation.id}`;
    } catch (err) {
      error.textContent = err.message;
      error.hidden = false;
      send.disabled = false;
    }
  });
  document.body.append(dialog);
  dialog.showModal();
  text.select();
}

async function logout() {
  await api('/auth/logout', { method: 'POST' }).catch(() => {});
  location.href = 'login.html';
}

// ------------------------------------------------------------------ location

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

export function countryName(code) {
  if (!code) return 'Unknown';
  try {
    return regionNames.of(code) || code;
  } catch {
    return code;
  }
}

/** Flag image (flag emoji don't show on Windows). */
export function flag(code) {
  if (!code) return el('span', { class: 'flag flag-unknown', 'aria-hidden': 'true' });
  return el('img', { class: 'flag', src: `flags/${code.toLowerCase()}.svg`, alt: '', width: 20, height: 15, loading: 'lazy' });
}

/**
 * "Rancho Cucamonga, California" (with the country as tooltip), or the country alone. Visits
 * without a location show the city of the browser's time zone as an estimate.
 */
export function locationLabel(s) {
  if (s.country) {
    const place = [...new Set([s.city, s.region].filter(Boolean))]; // "Kingston", not "Kingston, Kingston"
    const text = place.length ? place.join(', ') : countryName(s.country);
    return el('span', { class: 'location', title: `${text}${place.length ? `, ${countryName(s.country)}` : ''}` }, flag(s.country), el('span', { class: 'location-text' }, text));
  }
  const tzCity = (s.timezone || '').split('/').pop()?.replace(/_/g, ' ');
  return el(
    'span',
    { class: 'location muted', title: 'Location unknown. Estimated from the browser time zone' },
    flag(null),
    tzCity ? `~ ${tzCity}` : 'Unknown',
  );
}

// ------------------------------------------------------------------ tags, errors, page speed

/**
 * Tag chips: "utm_campaign=summer" (from the address), "customer=246245" (from the page), or a label
 * the team added ("bug"). onRemove, when given, adds a × to the team's labels.
 */
export function tagChips(tags, max = Infinity, onRemove = null) {
  const hint = { url: ' (from the page address)', custom: ' (from the page)', signed: ' (verified: signed by the website’s server)', manual: ' (added by the team)' };
  const shown = tags.slice(0, max).map((t) => {
    const text = `${t.source === 'signed' ? '✓ ' : ''}${t.value ? `${t.key}=${t.value}` : t.key}`;
    return el(
      'span',
      { class: `tag tag-${t.source}`, title: `${text}${hint[t.source] || ''}` },
      text,
      onRemove && t.source === 'manual'
        ? el('button', { type: 'button', class: 'tag-remove', 'aria-label': `Remove tag ${text}`, onclick: () => onRemove(t.key) }, '×')
        : null,
    );
  });
  if (tags.length > max) {
    shown.push(el('span', { class: 'tag tag-more', title: tags.slice(max).map((t) => (t.value ? `${t.key}=${t.value}` : t.key)).join('\n') }, `+${tags.length - max}`));
  }
  return shown;
}

/** Star toggle shared by the list and the replay page. Read-only (no click) without permission. */
export function starButton(session, { canStar, onChange } = {}) {
  const button = el('button', { type: 'button', class: 'star' });
  const render = () => {
    button.textContent = session.starred ? '★' : '☆';
    button.classList.toggle('on', !!session.starred);
    button.setAttribute('aria-pressed', String(!!session.starred));
    button.title = canStar ? (session.starred ? 'Starred: click to unstar' : 'Star this recording (starred recordings are kept)') : session.starred ? 'Starred' : 'Not starred';
    button.disabled = !canStar;
  };
  button.addEventListener('click', async (e) => {
    e.stopPropagation(); // in the list, the row itself opens the recording
    const next = !session.starred;
    session.starred = next ? 1 : 0;
    render();
    try {
      await api(`/sessions/${encodeURIComponent(session.id)}/star`, { method: 'PUT', body: { starred: next } });
      onChange?.(next);
    } catch (err) {
      session.starred = next ? 0 : 1;
      render();
      window.alert(err.message);
    }
  });
  render();
  return button;
}

/** "Replay removed": the website's "Keep recordings" setting removed the replay; the visit's data stays. */
export function replayRemovedChip(session) {
  if (!session?.replay_removed_at) return null;
  return el('span', { class: 'tag tag-muted', title: `The replay was removed on ${dateTime(session.replay_removed_at)} (Settings → Recordings → Keep recordings). The visit's data is kept.` }, 'Replay removed');
}

export function errorChip(count) {
  if (!count) return null;
  return el('span', { class: 'tag tag-error', title: 'JavaScript errors on this visit' }, `${count} error${count === 1 ? '' : 's'}`);
}

// Google's Core Web Vitals thresholds: [good up to, poor above].
const VITAL_LIMITS = { ttfb: [800, 1800], fcp: [1800, 3000], lcp: [2500, 4000], cls: [0.1, 0.25], inp: [200, 500] };
export const VITAL_NAMES = {
  lcp: ['Largest paint (LCP)', 'When the main content appeared'],
  fcp: ['First paint (FCP)', 'When anything first appeared'],
  ttfb: ['Server response (TTFB)', 'Time until the server started answering'],
  inp: ['Responsiveness (INP)', 'Slowest reaction to a click or key press'],
  cls: ['Layout shift (CLS)', 'How much the page jumped around while loading'],
  load_ms: ['Fully loaded', 'Time until the page finished loading'],
};

export function vitalRating(key, value) {
  const limits = VITAL_LIMITS[key];
  if (!limits || value == null) return null;
  if (value <= limits[0]) return { tone: 'good', label: 'Good' };
  if (value <= limits[1]) return { tone: 'warn', label: 'Needs improvement' };
  return { tone: 'poor', label: 'Poor' };
}

export function formatVital(key, value) {
  if (value == null) return '–';
  if (key === 'cls') return value.toFixed(2);
  return value >= 1000 ? `${(value / 1000).toFixed(1)} s` : `${Math.round(value)} ms`;
}
